import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import { Iconify } from 'src/components/iconify';
import { PermissionButton } from 'src/routes/components/permission-button';
import { useCopyToClipboard } from 'src/hooks/use-copy-to-clipboard';
import type {
  AdminRef,
  OperationStatus,
  ProposalActors,
} from 'src/pages/admin/services/vaultOperationsService';

import {
  EXPLORER_ADDRESS_URL,
  OPERATIONS_NS as NS,
  OPERATIONS_PERMISSION_RESOURCE,
  OUTCOME_BUTTON_SX,
  RUNBOOK_URLS,
  STATUS_STYLES,
  type OperationsTab,
} from './constants';
import { adminName, formatInstant, middle } from './helpers';

/** Rule 4: local time, the exact UTC instant on hover. */
export const TimeText = ({ iso }: { iso: string | null | undefined }) => {
  if (!iso) return <>—</>;
  const local = formatInstant(iso);
  if (local === '—') return <>—</>;
  return (
    <Tooltip title={`${formatInstant(iso, 'UTC')} UTC`}>
      <span>{local}</span>
    </Tooltip>
  );
};

export const CopyButton = ({ value, label }: { value: string; label?: string }) => {
  const translate = useTranslate();
  const { copy } = useCopyToClipboard();
  const [done, setDone] = useState(false);
  return (
    <Tooltip title={translate(done ? `${NS}.copied` : `${NS}.copy`)}>
      <IconButton
        size="small"
        aria-label={label ?? translate(`${NS}.copy`)}
        onClick={async () => {
          setDone(await copy(value));
          setTimeout(() => setDone(false), 1500);
        }}
      >
        <Iconify icon="solar:copy-bold" width={16} />
      </IconButton>
    </Tooltip>
  );
};

/** A short glossary hover on a term of the page ("Cold account", "company payout account", "slippage"); never the only place a decision is explained. */
export const Term = ({
  term,
  children,
}: {
  term: 'cold' | 'payout' | 'slippage';
  children: React.ReactNode;
}) => {
  const translate = useTranslate();
  return (
    <Tooltip title={translate(`${NS}.glossary.${term}`)}>
      <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>{children}</span>
    </Tooltip>
  );
};

/** Rule 4: an address shortened, with copy and an explorer link (and, when `explorerText` is given, the link in words). */
export const AddressLink = ({
  address,
  explorerText,
}: {
  address: string | null | undefined;
  explorerText?: string;
}) => {
  const translate = useTranslate();
  if (!address) return <>—</>;
  return (
    <Stack direction="row" spacing={0.25} sx={{ alignItems: 'center', display: 'inline-flex' }}>
      <Tooltip title={address}>
        <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
          {middle(address, 6, 4)}
        </Typography>
      </Tooltip>
      <CopyButton value={address} label={translate(`${NS}.copyAddress`)} />
      <Tooltip title={translate(`${NS}.explorer`)}>
        <IconButton
          size="small"
          component="a"
          href={`${EXPLORER_ADDRESS_URL}${address}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={translate(`${NS}.explorer`)}
        >
          <Iconify icon="solar:link-bold" width={16} />
        </IconButton>
      </Tooltip>
      {explorerText && (
        <Link
          href={`${EXPLORER_ADDRESS_URL}${address}`}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          sx={{ ml: 0.5 }}
        >
          {explorerText}
        </Link>
      )}
    </Stack>
  );
};

/**
 * A translated sentence with React nodes where it has %{name} slots (an address with copy and explorer link in running text,
 * rule 4). The slots are filled with unique markers first, so the translation keeps its own word order.
 */
export const nodes = (
  translateWith: (values: Record<string, string>) => string,
  slots: Record<string, React.ReactNode>,
  plain: Record<string, string> = {}
): React.ReactNode => {
  const marker = (name: string) => `\u0001${name}\u0001`;
  const values: Record<string, string> = { ...plain };
  Object.keys(slots).forEach((name) => {
    values[name] = marker(name);
  });
  return translateWith(values)
    .split('\u0001')
    .map((part, i) =>
      // eslint-disable-next-line react/no-array-index-key
      i % 2 === 1 ? <span key={i}>{slots[part]}</span> : part
    );
};

/** Rule 3: colour + word + one-line hover. `detail` is the extra fact that goes with it (auto-refund in 18 h, held by …). */
export const StatusChip = ({
  status,
  detail,
}: {
  status: OperationStatus;
  detail?: string | null;
}) => {
  const translate = useTranslate();
  const style = STATUS_STYLES[status];
  const label = translate(`${NS}.status.${status}`);
  return (
    <Tooltip title={[translate(`${NS}.statusHint.${status}`), detail].filter(Boolean).join(' · ')}>
      <Chip
        size="small"
        color={style.color}
        variant={style.color === 'default' && !style.purple ? 'outlined' : 'filled'}
        label={detail ? `${label} — ${detail}` : label}
        sx={style.purple ? { bgcolor: '#7e57c2', color: '#fff' } : undefined}
      />
    </Tooltip>
  );
};

export const AdminText = ({ admin, id }: { admin?: AdminRef; id: string | null | undefined }) => (
  <span>{adminName(admin, id)}</span>
);

/**
 * Rule 2: Proposed by <name> <time> → Waiting for a second operator → Applied. A cancelled proposal ends the strip with
 * "Cancelled by <name>"; a proposal the viewer made says "Waiting for another operator to approve".
 */
export const ProgressStrip = ({
  proposal,
  youProposed,
}: {
  proposal: ProposalActors & { status: 'AWAITING_APPROVAL' | 'APPLIED' | 'CANCELLED' };
  youProposed: boolean;
}) => {
  const translate = useTranslate();
  const t = (key: string, options?: object) => translate(`${NS}.progress.${key}`, options);
  const applied = proposal.status === 'APPLIED';
  const cancelled = proposal.status === 'CANCELLED';
  const step = (label: React.ReactNode, state: 'done' | 'current' | 'todo') => (
    <Chip
      size="small"
      variant={state === 'current' ? 'filled' : 'outlined'}
      color={state === 'done' ? 'success' : state === 'current' ? 'warning' : 'default'}
      label={label}
    />
  );
  return (
    <Stack
      direction="row"
      spacing={0.75}
      useFlexGap
      data-testid="progress-strip"
      sx={{ alignItems: 'center', flexWrap: 'wrap' }}
    >
      {step(
        <>
          {t('proposedBy')} <AdminText admin={proposal.proposedByAdmin} id={proposal.proposedBy} />{' '}
          <TimeText iso={proposal.proposedAt} />
        </>,
        'done'
      )}
      <Iconify icon="eva:arrow-ios-forward-fill" width={16} />
      {cancelled
        ? step(
            <>
              {t('cancelledBy')}{' '}
              <AdminText admin={proposal.cancelledByAdmin} id={proposal.cancelledBy} />{' '}
              <TimeText iso={proposal.cancelledAt} />
            </>,
            'done'
          )
        : step(
            applied ? (
              <>
                {t('approvedBy')}{' '}
                <AdminText admin={proposal.approvedByAdmin} id={proposal.approvedBy} />{' '}
                <TimeText iso={proposal.approvedAt} />
              </>
            ) : youProposed ? (
              t('waitingOther')
            ) : (
              t('waitingYou')
            ),
            applied ? 'done' : 'current'
          )}
      {!cancelled && (
        <>
          <Iconify icon="eva:arrow-ios-forward-fill" width={16} />
          {step(applied ? t('applied') : t('notYetApplied'), applied ? 'done' : 'todo')}
        </>
      )}
    </Stack>
  );
};

/** Rule 1: engine codes only here, collapsed, with a copy button. */
export const TechnicalDetails = ({ details }: { details: Record<string, unknown> }) => {
  const translate = useTranslate();
  const rows = Object.entries(details).filter(([, v]) => v !== null && v !== undefined && v !== '');
  const json = JSON.stringify(Object.fromEntries(rows), null, 2);
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}
    >
      <AccordionSummary
        expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" width={16} />}
        sx={{ px: 0, minHeight: 32 }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {translate(`${NS}.card.technical`)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0 }}>
        <Stack direction="row" sx={{ alignItems: 'flex-start' }}>
          <Box
            component="pre"
            sx={{ m: 0, flex: 1, fontSize: 12, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
          >
            {json}
          </Box>
          <CopyButton value={json} label={translate(`${NS}.card.copyTechnical`)} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

/** Rule 5: the collapsed "What is this queue?" paragraph and the tab's runbook. */
export const TabIntro = ({ tab }: { tab: OperationsTab }) => {
  const translate = useTranslate();
  const runbook = RUNBOOK_URLS[tab];
  return (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ color: 'text.secondary' }} data-testid={`lead-${tab}`}>
        {translate(`${NS}.lead.${tab}`)}
      </Typography>
      <Accordion disableGutters variant="outlined" data-testid={`intro-${tab}`}>
        <AccordionSummary expandIcon={<Iconify icon="eva:arrow-ios-downward-fill" width={16} />}>
          <Typography variant="subtitle2">{translate(`${NS}.intro.title`)}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1}>
            <Typography variant="body2">{translate(`${NS}.intro.${tab}`)}</Typography>
            {runbook && (
              <Link href={runbook} target="_blank" rel="noopener noreferrer" variant="body2">
                {translate(`${NS}.intro.runbook`)}
              </Link>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

/** Rule 5: "Nothing needs you. Last checked 10:42." — never a blank table. */
export const EmptyState = ({ tab, checkedAt }: { tab: OperationsTab; checkedAt?: string }) => {
  const translate = useTranslate();
  return (
    <Alert severity="success" data-testid={`empty-${tab}`}>
      <Stack spacing={0.25}>
        <Typography variant="subtitle2">
          {translate(`${NS}.empty.${tab}`)}
          {checkedAt && ` ${translate(`${NS}.lastChecked`, { time: checkedTime(checkedAt) })}`}
        </Typography>
      </Stack>
    </Alert>
  );
};

/** HH:mm in the viewer's zone of the instant the BFF read the engine. */
export const checkedTime = (iso: string): string => {
  const local = formatInstant(iso);
  return local === '—' ? local : local.slice(11);
};

/** `consistent: false` and `truncated`, in the words of XBIT-4870. */
export const ListNotices = ({
  consistent,
  truncated,
  shown,
  onRefresh,
}: {
  consistent?: boolean;
  truncated?: boolean;
  shown: number;
  onRefresh: () => void;
}) => {
  const translate = useTranslate();
  return (
    <>
      {consistent === false && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" sx={OUTCOME_BUTTON_SX} onClick={onRefresh}>
              {translate(`${NS}.refresh`)}
            </Button>
          }
        >
          {translate(`${NS}.notices.inconsistent`)}
        </Alert>
      )}
      {truncated && (
        <Alert severity="info">{translate(`${NS}.notices.truncated`, { n: shown })}</Alert>
      )}
    </>
  );
};

/** Rule 1: what happened · who is affected · what to do · deadline, in that order; codes only under "Technical details". */
export const ItemCard = ({
  title,
  status,
  statusDetail,
  whatHappened,
  affected,
  extra,
  deadline,
  actions,
  technical,
  testId,
}: {
  title: React.ReactNode;
  status: OperationStatus;
  statusDetail?: string | null;
  whatHappened: React.ReactNode;
  affected: React.ReactNode;
  extra?: React.ReactNode;
  deadline: React.ReactNode;
  actions: React.ReactNode;
  technical: Record<string, unknown>;
  testId: string;
}) => {
  const translate = useTranslate();
  const row = (label: string, body: React.ReactNode) => (
    <Stack spacing={0.25}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ overflowWrap: 'anywhere' }}>
        {body}
      </Typography>
    </Stack>
  );
  return (
    <Card variant="outlined" sx={{ p: 2 }} data-testid={testId}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
        >
          <Typography variant="subtitle1" component="div">
            {title}
          </Typography>
          <StatusChip status={status} detail={statusDetail} />
        </Stack>
        {row(translate(`${NS}.card.whatHappened`), whatHappened)}
        {row(translate(`${NS}.card.affected`), affected)}
        {extra}
        <Stack spacing={0.5}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {translate(`${NS}.card.whatToDo`)}
          </Typography>
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {actions}
          </Stack>
        </Stack>
        {row(translate(`${NS}.card.deadline`), deadline)}
        <TechnicalDetails details={technical} />
      </Stack>
    </Card>
  );
};

/** An outcome button, shown only to an operator whose role grants `action` on the console (read / propose / approve). */
export const ActionButton = ({
  needs,
  children,
  ...props
}: {
  needs: 'propose' | 'approve';
} & React.ComponentProps<typeof Button>) => (
  <PermissionButton resource={OPERATIONS_PERMISSION_RESOURCE} action={needs}>
    <Button {...props} sx={{ ...OUTCOME_BUTTON_SX, ...(props.sx as object) }}>
      {children}
    </Button>
  </PermissionButton>
);

/** The countdowns tick once a minute between reads. */
export const useNow = () => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  return now;
};
